import { ListItemText, Menu, MenuItem, Paper, Typography } from '@mui/material';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { EntitySecurity } from '../auth/Security';
import { SECURITY_ACTIONS } from '../auth/Security';
import { UserContext } from '../auth/UserContext';
import type { SecuredActionType } from '../components/ActionType';
import ResponsiveActions from '../components/ResponsiveActions';
import { StepsIndexConstant } from '../components/Stepper';
import { useStepper } from '../components/StepperContext';
import type { Entity } from '../constants/customSortFieldConfigs';
import { ENTITY_CONSTANTS } from '../constants/entityConstants';
import { DEFAULT_PAGE_CONFIG } from '../constants/reportConstants';
import { useGenerateReport } from '../generateReport/helpers/useGenerateReport';
import { useReportTemplatePreference, useSaveExistingReportTemplate } from '../generateReport/reportApiUtils';
import { handleDownloadPDF } from '../generateReport/reportUtils';
import I18n, { useI18n } from '../i18n/I18n';
import { Icons } from '../icons/Icons';
import { H, V } from '../Layout';
import { useStyling } from '../Theme';
import EmptyPromise from '../utils/EmptyPromise';
import { FORM_WIDGET_COMPONENTS } from './formConstants';
import { FormContext } from './FormContext';
import FormWidget from './FormWidget';

interface RenderLexicalEditorProps {
    isPreview?: boolean;
    isReport?: boolean;
    reportTitle?: string;
    onRegisteredExport?: (fn?: () => Promise<void>) => void;
    isJsonContent?: boolean;
    setSaveAsModalOpen?: (value: boolean) => void;
    entity: Entity;
    preferenceEntityKey: string;
    reportContextTerm?: string;
    reportTypeLabel?: string;
    /** Called with the rendered HTML, for callers that generate the document themselves. */
    onContentHtmlChange?: (html: string) => void;
}

export default function RenderLexicalEditor({
    isPreview = false,
    isReport = true,
    reportTitle = '',
    onRegisteredExport,
    isJsonContent = false,
    setSaveAsModalOpen,
    entity,
    preferenceEntityKey,
    reportContextTerm,
    reportTypeLabel,
    onContentHtmlChange
}: RenderLexicalEditorProps) {
    const { theme } = useStyling();
    const t = useI18n();
    const { state } = useContext(FormContext);
    const { generateReport } = useGenerateReport();
    const { activeOrganizationAccount, preferences, userProfile, refreshPreferences, application } =
        useContext(UserContext);
    const { matchingPreference, isViewOnly } = useReportTemplatePreference(preferences, state);
    const { getStepData } = useStepper();
    const richTextRef = useRef<HTMLDivElement>(null);
    const userAccountId = userProfile.accountId;
    const saveOptions = ['form.button.save', 'common.save.as'];

    const [isEditing, setIsEditing] = useState(false);
    const [editorHtml, setEditorHtml] = useState('');
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [menuOpened, setMenuOpened] = useState(false);
    const [initialContent, setInitialContent] = useState(state.rich);
    const tokenPrefix = isReport ? 'report' : 'resource.profile';

    const normalizedEntity = preferenceEntityKey.trim().toLowerCase();
    const defaultContextTerm = normalizedEntity ? `${normalizedEntity}StatusReport` : undefined;
    const contextTerm = reportContextTerm ?? defaultContextTerm;
    const resolvedReportTypeLabel = reportTypeLabel ?? t('resource.profile.default.type');

    const handleMenuClose = () => {
        setAnchorEl(null);
        setMenuOpened(false);
    };

    const { saveExistingReportTemplate } = useSaveExistingReportTemplate({
        matchingPreference,
        state,
        isViewOnly,
        userAccountId,
        application,
        setInitialContent,
        handleMenuClose,
        refreshFn: refreshPreferences,
        setIsEditing,
        entity,
        preferenceEntityKey
    });

    useEffect(() => {
        const stepData = getStepData(StepsIndexConstant.one);
        // Constructing pageConfig from stepData, falling back to defaults if needed
        const pageConfig = {
            pageLayout: stepData?.pageLayout || DEFAULT_PAGE_CONFIG.pageLayout,
            pageSize: stepData?.pageSize || DEFAULT_PAGE_CONFIG.pageSize,
            scale: Number(stepData?.scale) || DEFAULT_PAGE_CONFIG.scale
        };

        if (typeof onRegisteredExport === 'function' && editorHtml) {
            onRegisteredExport(() =>
                handleDownloadPDF({
                    generateReportFn: generateReport,
                    activeOrganizationAccount,
                    reportTitle,
                    content: editorHtml,
                    pageConfig,
                    contextTerm
                })
            );
            return () => onRegisteredExport(undefined);
        }
        return;
    }, [
        onRegisteredExport,
        editorHtml,
        generateReport,
        activeOrganizationAccount,
        reportTitle,
        getStepData,
        contextTerm
    ]);

    useEffect(() => {
        if (editorHtml) {
            onContentHtmlChange?.(editorHtml);
        }
    }, [editorHtml, onContentHtmlChange]);

    const hasChanges = useMemo(() => {
        const stringifiedState = typeof state.rich === 'string' ? state.rich : JSON.stringify(state.rich);
        return stringifiedState !== initialContent;
    }, [state.rich, initialContent]);

    const securedActions = useMemo<SecuredActionType[]>(() => {
        // READ (not EDIT): seed templates must still allow Edit + Save As.
        // Overwrite of the seed is blocked by isViewOnly on the Save menu item.
        const preferenceSecurity = matchingPreference
            ? {
                  securityAction: SECURITY_ACTIONS.READ,
                  entityName: ENTITY_CONSTANTS.preference,
                  entitySecurity: matchingPreference.security as EntitySecurity
              }
            : undefined;

        return [
            !isEditing && {
                label: <I18n token="form.title.edit" />,
                icon: Icons.Edit,
                onClick: () => setIsEditing(true),
                ...preferenceSecurity
            },
            isEditing &&
                hasChanges && {
                    label: <I18n token="form.title.save" />,
                    icon: Icons.Save,
                    onClick: (e) => {
                        setAnchorEl(e.currentTarget);
                        setMenuOpened(true);
                    },
                    ...preferenceSecurity
                }
        ].filter(Boolean) as SecuredActionType[];
    }, [isEditing, hasChanges, matchingPreference]);

    const handleMenuOptions = async (optionToken: string) => {
        if (optionToken === 'common.save.as') {
            if (typeof setSaveAsModalOpen === 'function') {
                setSaveAsModalOpen(true);
            }
            handleMenuClose();
            return;
        }

        if (optionToken === 'form.button.save') {
            const configureStepData = getStepData(StepsIndexConstant.one);
            await saveExistingReportTemplate(configureStepData);
        }
    };

    return (
        <V>
            <Paper
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    height: '100%',
                    p: 1,
                    position: 'relative',
                    boxShadow: 'none',
                    border: '1px solid #e0e0e0'
                }}
                elevation={0}
            >
                <H>
                    <H sx={{ justifyContent: 'space-between' }}>
                        <Typography
                            variant="h6"
                            color={theme.palette.primary.main}
                            fontWeight={theme.typography.fontWeightBold}
                        >
                            <I18n
                                token={`${tokenPrefix}.${isPreview ? 'preview' : 'editor'}`}
                                values={isReport ? undefined : { type: resolvedReportTypeLabel }}
                            />
                        </Typography>
                    </H>
                    {isEditing && (
                        <Menu anchorEl={anchorEl} keepMounted={false} open={menuOpened} onClose={handleMenuClose}>
                            {saveOptions.map((option, index) => {
                                const disabled = isViewOnly && option === 'form.button.save';
                                return (
                                    <MenuItem
                                        key={`option-${index}`}
                                        onClick={!disabled ? () => handleMenuOptions(option) : undefined}
                                        disabled={disabled}
                                    >
                                        <ListItemText>
                                            <I18n token={option} />
                                        </ListItemText>
                                    </MenuItem>
                                );
                            })}
                        </Menu>
                    )}
                    {!isPreview && (
                        <ResponsiveActions
                            isActivated
                            securedActions={securedActions}
                        />
                    )}
                </H>

                {/* container for rendered lexical content */}
                <div ref={richTextRef} style={{ overflow: 'auto', width: '100%', flex: 1 }}>
                    <FormWidget
                        id={'render-lexical-editor-rich'}
                        component={FORM_WIDGET_COMPONENTS.richText}
                        name={'rich'}
                        readOnly={!isEditing}
                        isSmall={false}
                        onSave={(title, html, event, json) => {
                            setIsEditing(false);
                            return EmptyPromise;
                        }}
                        onCancel={() => {
                            setIsEditing(false);
                            return EmptyPromise;
                        }}
                        sx={{
                            height: '100%',
                            minHeight: '100%',
                            border: 'none',
                            maxHeight: isPreview ? 'unset' : '360px'
                        }}
                        getHTMLFromContent={setEditorHtml}
                        isJsonContent={isJsonContent}
                        disableTranslation={true}
                    />
                </div>
            </Paper>
        </V>
    );
}
